import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Printer } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { familyQuery } from '@/features/portal/api'
import { childTimetableQuery } from '@/features/timetable/api'
import { TodayCard } from '@/features/timetable/components/TodayCard'
import { WeekGrid } from '@/features/timetable/components/WeekGrid'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'

function ChildTimetable({ studentId, name }: { studentId: number; name: string }) {
  const { t } = useTranslation('timetable')
  const { data, isPending, error, refetch } = useQuery(childTimetableQuery(studentId))
  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />
  const grid = data.grid
  if (!grid) {
    return (
      <Card>
        <EmptyState
          icon={<CalendarDays />}
          title={t('No current class')}
          description={t('{{name}} is not enrolled in a class this year.', { name })}
        />
      </Card>
    )
  }
  return (
    <div className="grid items-start gap-6 xl:grid-cols-[20rem_minmax(0,1fr)]">
      <TodayCard week={grid} view="section" />
      <Card data-print-document>
        <CardHeader
          title={t('{{section}} timetable', { section: grid.section.name })}
          description={t('{{year}} · {{count}} periods a week', {
            year: grid.academicYear.name,
            count: grid.totalPeriods,
          })}
          icon={<CalendarDays />}
          actions={
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<Printer />}
              onClick={() => window.print()}
              className="print:hidden"
            >
              {t('Print')}
            </Button>
          }
        />
        <div className="p-3 sm:p-4">
          {grid.entries.length === 0 ? (
            <EmptyState
              compact
              icon={<CalendarDays />}
              title={t('The timetable has not been published yet')}
            />
          ) : (
            <WeekGrid
              week={grid}
              view="section"
              caption={t('Weekly timetable of {{section}}', { section: grid.section.name })}
              highlightNow
            />
          )}
        </div>
      </Card>
    </div>
  )
}

export function FamilyTimetablePage() {
  const { t } = useTranslation('timetable')
  useDocumentTitle('Timetable')
  const children = useQuery(familyQuery())
  const [selected, setSelected] = useState(0)
  if (children.isPending) return <LoadingState />
  if (children.error) return <ErrorState onRetry={() => void children.refetch()} />
  const child = children.data[selected] ?? children.data[0]
  return (
    <>
      <PageHeader
        title={t('Timetable')}
        description={t('The class timetable for the week, with today highlighted.')}
      />
      {children.data.length > 1 ? (
        <div
          role="tablist"
          aria-label={t('Choose child')}
          className="mb-6 flex gap-2 overflow-x-auto print:hidden"
        >
          {children.data.map((c, i) => (
            <button
              key={c.student.id}
              role="tab"
              type="button"
              aria-selected={i === selected}
              onClick={() => setSelected(i)}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full border py-1 pr-4 pl-1 text-sm font-medium transition-colors',
                i === selected
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong',
              )}
            >
              <Avatar name={c.student.fullName} src={c.student.photoUrl} size="sm" />
              {c.student.firstName}
            </button>
          ))}
        </div>
      ) : null}
      {child ? (
        <ChildTimetable key={child.student.id} studentId={child.student.id} name={child.student.firstName} />
      ) : (
        <Card>
          <EmptyState
            icon={<CalendarDays />}
            title={t('No student linked')}
            description={t("Your account isn't linked to a student record yet.")}
          />
        </Card>
      )}
    </>
  )
}
